// =============================================================================
// MeuDriver — M2: Admin — Workflow de Aprovação de Instrutores
// =============================================================================

import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CheckCircle2, XCircle, Users, Clock } from 'lucide-react'
import {
  useInstructorApplications,
  useApproveInstructor,
  useRejectInstructor,
} from '@/hooks/useInstructorApproval'
import type { InstructorApplication } from '@/hooks/useInstructorApproval'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { getInitials, formatCurrency } from '@/lib/utils'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

function statusBadge(status: string) {
  switch (status) {
    case 'pending':
      return (
        <Badge className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">
          Aguardando
        </Badge>
      )
    case 'approved':
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
          Aprovado
        </Badge>
      )
    case 'rejected':
      return <Badge variant="destructive">Rejeitado</Badge>
    case 'suspended':
      return (
        <Badge className="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100">
          Suspenso
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

// ---------------------------------------------------------------------------
// Card de candidato
// ---------------------------------------------------------------------------

function ApplicantCard({
  instructor,
  showActions,
  onApprove,
  onReject,
}: {
  instructor: InstructorApplication
  showActions: boolean
  onApprove: (id: string) => void
  onReject: (id: string, name: string) => void
}) {
  const profile = instructor.profile
  const name = profile?.full_name ?? 'Sem nome'
  const city = profile?.city ?? null
  const state = profile?.state ?? null
  const location = [city, state].filter(Boolean).join(', ') || null

  const createdAt = format(parseISO(instructor.created_at), "dd 'de' MMMM 'de' yyyy", {
    locale: ptBR,
  })

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="p-5 flex flex-col sm:flex-row sm:items-start gap-4">
          {/* Avatar + info */}
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage src={profile?.avatar_url ?? undefined} />
            <AvatarFallback className="bg-brand-100 text-brand-700 font-semibold">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-gray-900 dark:text-white">{name}</p>
              {statusBadge(instructor.status)}
            </div>

            {location && (
              <p className="text-sm text-muted-foreground">{location}</p>
            )}

            {instructor.bio && (
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                {instructor.bio}
              </p>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Cadastro: {createdAt}
              </span>
              {instructor.years_experience != null && (
                <span>{instructor.years_experience} anos exp.</span>
              )}
              {instructor.hourly_rate != null && (
                <span>Valor/h: {formatCurrency(instructor.hourly_rate)}</span>
              )}
              {instructor.cnh_number && (
                <span>CNH: {instructor.cnh_number}</span>
              )}
            </div>

            {instructor.rejection_reason && (
              <div className="mt-2 rounded-lg bg-red-50 border border-red-100 p-2 text-xs text-red-700">
                <span className="font-medium">Motivo da rejeição:</span>{' '}
                {instructor.rejection_reason}
              </div>
            )}
          </div>

          {/* Ações */}
          {showActions && (
            <div className="flex gap-2 sm:flex-col shrink-0">
              <Button
                size="sm"
                data-testid="approve-btn"
                className="bg-green-600 hover:bg-green-700 gap-1.5"
                onClick={() => onApprove(instructor.id)}
              >
                <CheckCircle2 className="h-4 w-4" />
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid="reject-btn"
                className="border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
                onClick={() => onReject(instructor.id, name)}
              >
                <XCircle className="h-4 w-4" />
                Rejeitar
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Lista por aba
// ---------------------------------------------------------------------------

function InstructorList({
  status,
  showActions,
  onApprove,
  onReject,
}: {
  status: 'pending' | 'approved' | 'rejected' | 'suspended'
  showActions: boolean
  onApprove: (id: string) => void
  onReject: (id: string, name: string) => void
}) {
  const { data, isLoading } = useInstructorApplications(status)

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-36 w-full rounded-xl" />
        ))}
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="h-10 w-10 text-gray-300 mb-3" />
        <p className="font-medium text-gray-500">Nenhum instrutor nesta categoria.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {data.map((inst) => (
        <ApplicantCard
          key={inst.id}
          instructor={inst}
          showActions={showActions}
          onApprove={onApprove}
          onReject={onReject}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function AdminInstructorApproval() {
  const { data: pending } = useInstructorApplications('pending')
  const pendingCount = pending?.length ?? 0

  const { mutate: approve, isPending: approving } = useApproveInstructor()
  const { mutate: reject, isPending: rejecting } = useRejectInstructor()

  // Reject dialog state
  const [rejectDialog, setRejectDialog] = useState<{
    open: boolean
    instructorId: string
    instructorName: string
  }>({ open: false, instructorId: '', instructorName: '' })
  const [rejectionReason, setRejectionReason] = useState('')

  function handleApprove(id: string) {
    approve(id)
  }

  function handleOpenReject(id: string, name: string) {
    setRejectionReason('')
    setRejectDialog({ open: true, instructorId: id, instructorName: name })
  }

  function handleConfirmReject() {
    if (!rejectionReason.trim()) return
    reject(
      { instructorId: rejectDialog.instructorId, reason: rejectionReason },
      { onSuccess: () => setRejectDialog((s) => ({ ...s, open: false })) },
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Aprovação de Instrutores
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Revise e aprove ou rejeite candidaturas de instrutores.
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="pending" className="relative" data-testid="tab-pending">
            Pendentes
            {pendingCount > 0 && (
              <span
                className={cn(
                  'ml-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold',
                  'bg-orange-500 text-white',
                )}
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Aprovados</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejeitados</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="pending">
            <InstructorList
              status="pending"
              showActions
              onApprove={handleApprove}
              onReject={handleOpenReject}
            />
          </TabsContent>
          <TabsContent value="approved">
            <InstructorList
              status="approved"
              showActions={false}
              onApprove={handleApprove}
              onReject={handleOpenReject}
            />
          </TabsContent>
          <TabsContent value="rejected">
            <InstructorList
              status="rejected"
              showActions={false}
              onApprove={handleApprove}
              onReject={handleOpenReject}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Dialog de rejeição */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(o) => setRejectDialog((s) => ({ ...s, open: o }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar candidatura</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição de{' '}
              <strong>{rejectDialog.instructorName}</strong>. O instrutor será
              notificado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="rejection-reason">Motivo (obrigatório)</Label>
            <Textarea
              id="rejection-reason"
              data-testid="rejection-reason"
              rows={4}
              placeholder="Ex: Documentação incompleta, CNH inválida..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialog((s) => ({ ...s, open: false }))}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={!rejectionReason.trim() || rejecting}
              data-testid="confirm-reject-btn"
            >
              {rejecting ? 'Rejeitando...' : 'Confirmar rejeição'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
